import React, {
  createContext,
  useEffect,
  useReducer
} from 'react';
import jwtDecode from 'jwt-decode';
import SplashScreen from 'src/components/SplashScreen';
// import axios from 'src/utils/axios';
import axios from 'axios';
import { apiBaseURL } from '../system/Constants/API.js';

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null
};

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);

    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

const setUserSession = (user) => {
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  } else {
    localStorage.removeItem('user');
  }
}

const setSessionBranch = (branch) => {
  if (branch) {
    localStorage.setItem('selBranch', JSON.stringify(branch));
  } else {
    localStorage.removeItem('selBranch');
  }
};

const loadFloatForUser = async (user) => {
  // Only load float for POS users with floatActive
  if (user && user.floatActive && user.role === 'pos') {
    try {
      // Get the float document
      const response = await axios.get(
        `${apiBaseURL}/api/v1/monies/float/${user.floatActive}`
      );
      const floatDoc = response.data.doc;
      
      // Store simplified float object in localStorage
      if (floatDoc) {
        const floatObj = {
          id: floatDoc._id,
          branch: floatDoc.branch,
          cashRegister: floatDoc.cashRegister,
          user: floatDoc.user
        };
        localStorage.setItem('float', JSON.stringify(floatObj));
      }
    } catch (err) {
      console.error('[JWTAuthContext] Failed to load float:', err);
      localStorage.removeItem('float');
    }
  } else {
    // Clear float for non-POS users or users without floatActive
    localStorage.removeItem('float');
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    case 'REGISTER': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => { },
  register: () => Promise.resolve()
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const login = async (email, password) => {
    // TODO: DEVELOPMENT 
    // const response = await axios.post('/api/account/login', { email, password });
    // let { user, token } = response.data;
    // const selFirstBranch = {
    //   branchId: "",
    //   branchName: "",
    // }

    const response = await axios.post(`${apiBaseURL}/api/v1/users/login`, { email, password });
    const token = response.data.token;
    const user = response.data.data.user;
    const selFirstBranch = response.data.data.user.branches[0];
    

    console.log('[JWTAuthContext] : (AuthProvidoer) - response',response);

    setSession(token);
    setUserSession(user);
    setSessionBranch(selFirstBranch);
    
    // Load float if user is POS and has floatActive
    await loadFloatForUser(user);
    
    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
  };

  const logout = () => {
    setSession(null);
    setSessionBranch(null);
    localStorage.clear();
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (email, name, password) => {
    const response = await axios.post('/api/account/register', {
      email,
      name,
      password
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  useEffect(() => {
    const initialise = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          //const response = await axios.get('/api/account/me');
          const response = await axios.get(`${apiBaseURL}/api/v1/users/me`);
          const user = response.data.doc;

          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: true,
              user
            }
          });
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  // this state
  //console.log('Authentication State :: ',state);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        register
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;